import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  UsersRound,
  Building2,
  Receipt,
  Landmark,
  ShieldCheck,
  PiggyBank,
  TrendingUp,
  FolderOpen,
  Headset,
  Coins,
  BarChart3,
  BookOpen,
  UserCog,
  Settings,
  LogOut,
  X,
  FileSpreadsheet,
  FileText,
  CalendarDays
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import midasLogo from '../assets/branding/midas-logo.png';
import '../styles/Sidebar.css';

const MENU_CONFIG = [
  {
    group: 'General',
    items: [{ label: 'Tablero Principal', path: '/dashboard', icon: LayoutDashboard }]
  },
  {
    group: 'Operación',
    items: [
      { label: 'Clientes', path: '/clientes', icon: Users, permission: 'CLIENTES_VER' },
      { label: 'Cartera de Créditos', path: '/creditos', icon: CreditCard, permission: 'CREDITOS_VER' },
      { label: 'Grupos', path: '/grupos', icon: UsersRound, permission: 'AGRUPACIONES_VER' },
      { label: 'Colocadoras', path: '/colocadoras', icon: Building2, permission: 'AGRUPACIONES_VER' },
      { label: 'Gestión de Cobranza', path: '/cobranza', icon: Coins, permission: 'COBRANZA_VER' },
      { label: 'Control de Pagos', path: '/pagos', icon: Receipt, permission: 'PAGOS_VER' }
    ]
  },
  {
    group: 'Reportes',
    items: [
      { label: 'Cobranza del Día', path: '/reportes/cobranza-diaria', icon: FileSpreadsheet, permission: 'COBRANZA_VER' },
      { label: 'Cierre de Caja', path: '/reportes/cierre-caja', icon: Receipt, permission: 'COBRANZA_VER' },
      { label: 'Corte del Día', icon: FileText, soon: true },
      { label: 'Cobranza Semanal', icon: CalendarDays, soon: true }
    ]
  },

  {
    group: 'Finanzas',
    items: [
      { label: 'Bancos', icon: Landmark, soon: true },
      { label: 'Pólizas', icon: ShieldCheck, soon: true },
      { label: 'Ahorros', icon: PiggyBank, soon: true },
      { label: 'Inversiones', icon: TrendingUp, soon: true }
    ]
  },
  {
    group: 'Gestión',
    items: [
      { label: 'Expediente Digital', icon: FolderOpen, soon: true },
      { label: 'CRM', icon: Headset, soon: true }
    ]
  },
  {
    group: 'Análisis',
    items: [
      { label: 'Métricas', icon: BarChart3, soon: true }
    ]
  },
  {
    group: 'Configuración',
    items: [
      { label: 'Catálogos', path: '/catalogos', icon: BookOpen },
      { label: 'Usuarios', path: '/usuarios', icon: UserCog, permission: 'USUARIOS_VER' },
      { label: 'Sistema', icon: Settings, soon: true }
    ]
  }
];

function Sidebar({ isOpen, onClose }) {
  const { hasPermission, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onClose) onClose();
  };

  // Filtrar grupos e items según permisos del usuario
  const menuFiltrado = MENU_CONFIG.map(({ group, items }) => ({
    group,
    items: items.filter((item) => {
      if (item.soon) return true;
      if (!item.permission) return true;
      return hasPermission(item.permission);
    })
  })).filter((group) => group.items.length > 0);

  return (
    <>
      <div
        className={`sidebar-scrim ${isOpen ? 'is-visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`sidebar ${isOpen ? 'is-open' : ''}`}>
        <div className="sidebar-brand">
          <button className="sidebar-close" onClick={onClose} aria-label="Cerrar menú">
            <X size={18} strokeWidth={2} />
          </button>

          <img src={midasLogo} alt="MIDAS" className="sidebar-logo" />

          <span className="sidebar-brand-caption">Sistema MIDAS</span>
        </div>

        <nav className="sidebar-nav" aria-label="Navegación principal">
          {menuFiltrado.map(({ group, items }) => (
            <div key={group} className="nav-group">
              <p className="nav-group-label">{group}</p>

              {items.map(({ label, path, icon: Icon, soon }) =>
                soon ? (
                  <div key={label} className="nav-item is-soon" aria-disabled="true">
                    <Icon className="nav-icon" size={18} strokeWidth={1.75} />
                    <span className="nav-label">{label}</span>
                    <span className="nav-badge" title="Módulo aún no disponible">
                      Pronto
                    </span>
                  </div>
                ) : (
                  <NavLink
                    key={label}
                    to={path}
                    className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}
                    onClick={onClose}
                  >
                    <Icon className="nav-icon" size={18} strokeWidth={1.75} />
                    <span className="nav-label">{label}</span>
                  </NavLink>
                )
              )}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-logout" type="button" onClick={handleLogout}>
            <LogOut size={17} strokeWidth={1.75} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
